// Turns the flat app × geo report into the two-level view the page renders:
// one group per app (or, for traffic that belongs to no app, per tracker
// campaign) with its geos underneath.
//
// The flat table was 1400 rows of which 95% were geos below the noise floor —
// Firebase reports arrivals for every country GA4 has ever seen, so most rows
// carry three clicks and no verdict. Grouping puts the app's own numbers first
// and folds that tail away instead of scrolling through it.

import type { PerfRow, PerfVerdict, NoAppPerfRow } from '@/api/perf';

export interface GeoLine {
  key: string;
  country: string;
  clicks: number;
  uniqClicks: number;
  regs: number;
  deps: number;
  payoutCents: number;
  fbNewUsers: number | null;
  arrival: number | null;
  regRate: number | null;
  depRate: number | null;
  epcCents: number | null;
  baseRegRate: number | null;
  baseFbNewUsers: number | null;
  lostRegs: number;
  lostMoneyCents: number;
  verdict: PerfVerdict;
  noisy: boolean;
  fbMissing: boolean;
  /** The source row, for the per-geo drill-down. Absent for no-app traffic. */
  row: PerfRow | null;
}

export interface PerfGroup {
  key: string;
  name: string;
  subtitle: string | null;
  clicks: number;
  uniqClicks: number;
  regs: number;
  deps: number;
  payoutCents: number;
  fbNewUsers: number | null;
  arrival: number | null;
  regRate: number | null;
  depRate: number | null;
  epcCents: number | null;
  baseClicks: number;
  baseRegs: number;
  baseDeps: number;
  basePayoutCents: number;
  baseFbNewUsers: number | null;
  baseRegRate: number | null;
  baseDepRate: number | null;
  lostRegs: number;
  lostMoneyCents: number;
  /** Geos carrying a verdict or a measured loss — the badge on the group row. */
  issues: number;
  crashFreePct: number | null;
  hasFirebase: boolean;
  /** Removed from the store — kept visible, but easy to filter out. */
  isBanned: boolean;
  /** Geos worth showing, most expensive first. */
  geos: GeoLine[];
  /** Geos folded away — kept so the row can be unfolded on demand. */
  hidden: GeoLine[];
  hiddenGeos: number;
  hiddenClicks: number;
}

export interface PerfTotals {
  clicks: number;
  uniqClicks: number;
  regs: number;
  deps: number;
  payoutCents: number;
  fbNewUsers: number | null;
  arrival: number | null;
  regRate: number | null;
  depRate: number | null;
  epcCents: number | null;
  baseClicks: number;
  baseRegs: number;
  baseDeps: number;
  basePayoutCents: number;
  baseFbNewUsers: number | null;
  lostMoneyCents: number;
  lostRegs: number;
  issues: number;
  groups: number;
}

export interface GroupOptions {
  /** Below this many clicks a geo is folded away unless it carries a verdict. */
  minClicks: number;
  /** Keep only geos with a verdict or a measured loss. */
  issuesOnly: boolean;
  /** Drop apps removed from the store: their geos cannot be acted on. */
  hideBanned?: boolean;
}

function rate(num: number, den: number): number | null {
  return den > 0 ? num / den : null;
}

/** A geo earns a line of its own by having volume, a verdict, or a loss. */
function isSignificant(g: GeoLine, minClicks: number): boolean {
  return g.clicks >= minClicks || g.verdict !== 'none' || g.lostRegs > 0;
}

function hasIssue(g: GeoLine): boolean {
  return g.verdict !== 'none' || g.lostRegs > 0;
}

function byLoss(a: GeoLine, b: GeoLine): number {
  return b.lostMoneyCents - a.lostMoneyCents || b.clicks - a.clicks;
}

function toGeoLine(r: PerfRow): GeoLine {
  return {
    key: `${r.appId}|${r.country}`,
    country: r.country,
    clicks: r.clicks,
    uniqClicks: r.uniqClicks,
    regs: r.regs,
    deps: r.deps,
    payoutCents: r.payoutCents,
    fbNewUsers: r.fbNewUsers,
    arrival: r.arrival,
    regRate: r.regRate,
    depRate: r.depRate,
    epcCents: r.epcCents,
    baseRegRate: r.baseRegRate,
    baseFbNewUsers: r.baseFbNewUsers,
    lostRegs: r.lostRegs,
    lostMoneyCents: r.lostMoneyCents,
    verdict: r.verdict,
    noisy: r.noisy,
    fbMissing: r.fbMissing,
    row: r,
  };
}

/**
 * Aggregate one group's geos. Totals always cover EVERY geo of the group — the
 * app's real week does not shrink because a filter hid three of its rows — while
 * the visible list obeys the filter.
 */
function buildGroup(
  key: string,
  name: string,
  subtitle: string | null,
  all: GeoLine[],
  extra: {
    hasFirebase: boolean;
    isBanned: boolean;
    crashFreePct: number | null;
    baseClicks: number; baseRegs: number; baseDeps: number;
    basePayoutCents: number; baseFbNewUsers: number | null;
  },
  opts: GroupOptions,
): PerfGroup {
  const sum = (pick: (g: GeoLine) => number) => all.reduce((s, g) => s + pick(g), 0);
  const clicks = sum((g) => g.clicks);
  const regs = sum((g) => g.regs);
  const deps = sum((g) => g.deps);
  const payoutCents = sum((g) => g.payoutCents);

  // A group's arrivals are known only where Firebase answered; summing the
  // nulls as zero would invent a delivery problem.
  const fbKnown = all.filter((g) => g.fbNewUsers != null);
  const fbNewUsers = extra.hasFirebase && fbKnown.length > 0
    ? fbKnown.reduce((s, g) => s + (g.fbNewUsers ?? 0), 0)
    : null;

  const visible = (opts.issuesOnly ? all.filter(hasIssue) : all.filter((g) => isSignificant(g, opts.minClicks)))
    .slice()
    .sort(byLoss);
  const hidden = all.filter((g) => !visible.includes(g));

  return {
    key,
    name,
    subtitle,
    clicks,
    uniqClicks: sum((g) => g.uniqClicks),
    regs,
    deps,
    payoutCents,
    fbNewUsers,
    arrival: fbNewUsers == null ? null : rate(fbNewUsers, clicks),
    regRate: rate(regs, clicks),
    depRate: rate(deps, regs),
    epcCents: rate(payoutCents, clicks),
    baseClicks: extra.baseClicks,
    baseRegs: extra.baseRegs,
    baseDeps: extra.baseDeps,
    basePayoutCents: extra.basePayoutCents,
    baseFbNewUsers: extra.baseFbNewUsers,
    baseRegRate: rate(extra.baseRegs, extra.baseClicks),
    baseDepRate: rate(extra.baseDeps, extra.baseRegs),
    lostRegs: sum((g) => g.lostRegs),
    lostMoneyCents: sum((g) => g.lostMoneyCents),
    issues: all.filter(hasIssue).length,
    crashFreePct: extra.crashFreePct,
    hasFirebase: extra.hasFirebase,
    isBanned: extra.isBanned,
    geos: visible,
    hidden: hidden.slice().sort(byLoss),
    hiddenGeos: hidden.length,
    hiddenClicks: hidden.reduce((s, g) => s + g.clicks, 0),
  };
}

/** Most expensive problem first — the top group answers "where are we losing". */
function byGroupLoss(a: PerfGroup, b: PerfGroup): number {
  return b.lostMoneyCents - a.lostMoneyCents
    || b.payoutCents - a.payoutCents
    || b.clicks - a.clicks;
}

export function groupAppRows(rows: PerfRow[], opts: GroupOptions): PerfGroup[] {
  const byApp = new Map<string, PerfRow[]>();
  for (const r of rows) {
    if (opts.hideBanned && r.isBanned) continue;
    const list = byApp.get(r.appId);
    if (list) list.push(r); else byApp.set(r.appId, [r]);
  }

  const out: PerfGroup[] = [];
  for (const [appId, appRows] of byApp) {
    const first = appRows[0]!;
    const fbBase = appRows.filter((r) => r.baseFbNewUsers != null);
    const group = buildGroup(
      appId, first.appName, first.bundleId,
      appRows.map(toGeoLine),
      {
        hasFirebase: appRows.some((r) => r.hasFirebase),
        isBanned: first.isBanned,
        crashFreePct: appRows.find((r) => r.crashFreePct != null)?.crashFreePct ?? null,
        baseClicks: appRows.reduce((s, r) => s + r.baseClicks, 0),
        baseRegs: appRows.reduce((s, r) => s + r.baseRegs, 0),
        baseDeps: appRows.reduce((s, r) => s + r.baseDeps, 0),
        basePayoutCents: appRows.reduce((s, r) => s + r.basePayoutCents, 0),
        baseFbNewUsers: fbBase.length > 0 ? fbBase.reduce((s, r) => s + (r.baseFbNewUsers ?? 0), 0) : null,
      },
      opts,
    );
    if (opts.issuesOnly && group.geos.length === 0) continue;
    out.push(group);
  }
  return out.sort(byGroupLoss);
}

/**
 * Traffic bound to no app: PWA and landing campaigns. No Firebase, no verdicts —
 * arrivals and the reachable bar both need an app behind them — so these groups
 * carry the funnel and the money only.
 */
export function groupNoAppRows(rows: NoAppPerfRow[], opts: GroupOptions): PerfGroup[] {
  const byCampaign = new Map<string, NoAppPerfRow[]>();
  for (const r of rows) {
    const list = byCampaign.get(r.campaignId);
    if (list) list.push(r); else byCampaign.set(r.campaignId, [r]);
  }

  const out: PerfGroup[] = [];
  for (const [campaignId, campRows] of byCampaign) {
    const first = campRows[0]!;
    const geos: GeoLine[] = campRows.map((r) => ({
      key: `${r.campaignId}|${r.country}`,
      country: r.country,
      clicks: r.clicks,
      uniqClicks: r.uniqClicks,
      regs: r.regs,
      deps: r.deps,
      payoutCents: r.payoutCents,
      fbNewUsers: null,
      arrival: null,
      regRate: rate(r.regs, r.clicks),
      depRate: rate(r.deps, r.regs),
      epcCents: rate(r.payoutCents, r.clicks),
      baseRegRate: null,
      baseFbNewUsers: null,
      lostRegs: 0,
      lostMoneyCents: 0,
      verdict: 'none',
      noisy: r.clicks < opts.minClicks,
      fbMissing: false,
      row: null,
    }));
    const subtitle = [first.label, first.destinationType].filter(Boolean).join(' · ') || null;
    out.push(buildGroup(
      campaignId, first.campaignName || '(deleted campaign)', subtitle, geos,
      {
        hasFirebase: false, isBanned: false, crashFreePct: null,
        baseClicks: 0, baseRegs: 0, baseDeps: 0, basePayoutCents: 0, baseFbNewUsers: null,
      },
      // Nothing here can carry a verdict, so the issues filter would empty the
      // whole section; the noise floor still applies.
      { ...opts, issuesOnly: false },
    ));
  }
  return out.sort((a, b) => b.clicks - a.clicks);
}

export function sumGroups(groups: PerfGroup[]): PerfTotals {
  const sum = (pick: (g: PerfGroup) => number) => groups.reduce((s, g) => s + pick(g), 0);
  const clicks = sum((g) => g.clicks);
  const regs = sum((g) => g.regs);
  const deps = sum((g) => g.deps);
  const payoutCents = sum((g) => g.payoutCents);
  const fbKnown = groups.filter((g) => g.fbNewUsers != null);
  const fbNewUsers = fbKnown.length > 0 ? fbKnown.reduce((s, g) => s + (g.fbNewUsers ?? 0), 0) : null;
  const baseFbKnown = groups.filter((g) => g.baseFbNewUsers != null);

  return {
    clicks,
    uniqClicks: sum((g) => g.uniqClicks),
    regs,
    deps,
    payoutCents,
    fbNewUsers,
    arrival: fbNewUsers == null ? null : rate(fbNewUsers, clicks),
    regRate: rate(regs, clicks),
    depRate: rate(deps, regs),
    epcCents: rate(payoutCents, clicks),
    baseClicks: sum((g) => g.baseClicks),
    baseRegs: sum((g) => g.baseRegs),
    baseDeps: sum((g) => g.baseDeps),
    basePayoutCents: sum((g) => g.basePayoutCents),
    baseFbNewUsers: baseFbKnown.length > 0 ? baseFbKnown.reduce((s, g) => s + (g.baseFbNewUsers ?? 0), 0) : null,
    lostMoneyCents: sum((g) => g.lostMoneyCents),
    lostRegs: sum((g) => g.lostRegs),
    issues: sum((g) => g.issues),
    groups: groups.length,
  };
}
